import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const subnavigationSelectors = [
  '.lss-systems-jump',
  '.lss-equipment-jump',
  '.lss-development-jump',
  '.lss-about__nav',
  '.lss-product-record__nav',
];

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateAnchorOffsets);
const headerStateObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(updateAnchorOffsets);

function visibleSubnavigation() {
  return subnavigationSelectors
    .map((selector) => document.querySelector(selector))
    .find((element) => element && element.getClientRects().length > 0);
}

function persistentHeaderSections(header) {
  return [...(header?.children ?? [])].filter((element) => {
    if (!(element instanceof HTMLElement)) return false;

    const headerComponent = element.querySelector(':scope > #header-component');
    const stickyMode = headerComponent?.getAttribute('sticky');

    return stickyMode === 'always' ||
      (stickyMode === 'scroll-up' && headerComponent?.dataset.stickyState === 'active');
  });
}

function updateAnchorOffsets() {
  const header = document.getElementById('header-group');
  const subnavigation = visibleSubnavigation();
  const headerHeight = Math.max(0, Math.ceil(
    persistentHeaderSections(header).reduce(
      (height, element) => height + element.getBoundingClientRect().height,
      0,
    ),
  ));
  const subnavigationHeight = Math.max(0, Math.ceil(subnavigation?.getBoundingClientRect().height ?? 0));

  root.style.setProperty('--lss-header-stack-height', `${headerHeight}px`);
  root.style.setProperty('--lss-subnav-height', `${subnavigationHeight}px`);

}

function refreshObservedOffsets() {
  updateAnchorOffsets();
  resizeObserver?.disconnect();
  headerStateObserver?.disconnect();

  const header = document.getElementById('header-group');
  const subnavigation = visibleSubnavigation();
  for (const element of header?.children ?? []) resizeObserver?.observe(element);
  if (subnavigation) resizeObserver?.observe(subnavigation);
  if (header) {
    headerStateObserver?.observe(header, {
      attributes: true,
      attributeFilter: ['data-sticky-state', 'sticky'],
      subtree: true,
    });
  }
}

function targetFromHash(hash = window.location.hash) {
  if (!hash || hash === '#') return null;

  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch (_) {
    return document.getElementById(hash.slice(1));
  }
}

function anchorOffset(container) {
  // Read the resolved CSS length so rem gaps and root font-size changes stay in sync.
  const offset = Number.parseFloat(getComputedStyle(container).scrollPaddingTop);
  return Number.isFinite(offset) ? offset : 0;
}

function scrollToTarget(target, behavior = 'instant') {
  updateAnchorOffsets();
  const container = getScrollContainer();
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const containerTop = container.classList.contains('page-wrapper') ? containerRect.top : 0;
  const top = Math.max(0, container.scrollTop + targetRect.top - containerTop - anchorOffset(container));
  container.scrollTo({ top, behavior });
}

function focusTarget(target) {
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

function hasSavedScrollPosition() {
  const scrollTop = history.state?.scrollTop;
  return Number.isFinite(scrollTop) && scrollTop >= 0;
}

function restoreCurrentHash(event) {
  // scroll-container owns saved history positions. A retained fragment identifies the
  // original jump, not necessarily the place the visitor was reading when they left.
  // A bfcache return also already has its document/scroll state restored.
  if (event?.persisted || hasSavedScrollPosition()) return;

  const target = targetFromHash();
  if (!target) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // A history traversal or another fragment request can happen while frames wait.
      if (!hasSavedScrollPosition() && target === targetFromHash()) {
        scrollToTarget(target, 'instant');
      }
    });
  });
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a[href*="#"]');
  if (!link || link.target || link.hasAttribute('download')) return;

  const url = new URL(link.href, window.location.href);
  const sameDocument =
    url.origin === window.location.origin &&
    url.pathname === window.location.pathname &&
    url.search === window.location.search;
  const target = sameDocument ? targetFromHash(url.hash) : null;
  if (!target) return;

  event.preventDefault();
  if (url.hash !== window.location.hash) {
    history.pushState({}, '', url.hash);
  }
  scrollToTarget(target, reducedMotion.matches ? 'instant' : 'smooth');
  focusTarget(target);
});

window.addEventListener('hashchange', restoreCurrentHash);
window.addEventListener('popstate', restoreCurrentHash);
window.addEventListener('pageshow', restoreCurrentHash);
window.addEventListener('load', restoreCurrentHash);
window.addEventListener('resize', updateAnchorOffsets, { passive: true });
scrollContainerMediaQuery.addEventListener('change', () => {
  refreshObservedOffsets();
  restoreCurrentHash();
});
document.addEventListener('shopify:section:load', () => {
  refreshObservedOffsets();
  restoreCurrentHash();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    refreshObservedOffsets();
    restoreCurrentHash();
  }, { once: true });
} else {
  refreshObservedOffsets();
  restoreCurrentHash();
}
